import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  type ConviteGerado,
  type ListaUsuarios,
  type Papel,
  type UsuarioCriado,
  type UsuarioNaLista,
  api,
  explicarMotivo,
} from '../lib/api.js';
import { BlocoDeAjuda } from './ui.js';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

/**
 * Gestão de usuários — só admin.
 *
 * Antes, dar acesso a um instrutor dependia de alguém com terminal do
 * container. Aqui a secretaria resolve sozinha.
 *
 * A tela NÃO oferece o que o servidor vai recusar (botão de desativar a si
 * mesmo, seletor do próprio papel), mas isso é cortesia, não proteção: as
 * três regras estão no serviço, e uma requisição direta bate na mesma parede.
 */

const PAPEIS: { valor: Papel; rotulo: string; descricao: string }[] = [
  { valor: 'leitor', rotulo: 'Leitor', descricao: 'vê tudo, não edita nada' },
  { valor: 'editor', rotulo: 'Editor', descricao: 'edita FAQ, resolve lacunas e transbordos' },
  { valor: 'admin', rotulo: 'Admin', descricao: 'tudo, mais reindexação e usuários' },
];

function data(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('pt-BR') : 'nunca';
}

export function Usuarios(): JSX.Element {
  const [dados, setDados] = useState<ListaUsuarios | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  /** Convite recém-gerado. Fica na tela até o admin fechar. */
  const [convite, setConvite] = useState<{ gerado: ConviteGerado; nome: string } | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.get<ListaUsuarios>('/admin/usuarios'));
      setErro(null);
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao carregar os usuários'));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function alternarAtivo(u: UsuarioNaLista): Promise<void> {
    try {
      await api.post(`/admin/usuarios/${u.id}/ativacao`, { ativo: !u.ativo });
      await carregar();
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao alterar o status'));
    }
  }

  async function reenviarConvite(u: UsuarioNaLista): Promise<void> {
    try {
      const gerado = await api.post<ConviteGerado>(`/admin/usuarios/${u.id}/convite`);
      setConvite({ gerado, nome: u.nome });
      setErro(null);
      await carregar();
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao gerar o convite'));
    }
  }

  if (!dados) {
    return erro ? (
      <Alert color="red" variant="light" role="alert">
        {erro}
      </Alert>
    ) : (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  return (
    <Stack gap="sm">
      {convite && (
        <LinkDeConvite
          convite={convite.gerado}
          nome={convite.nome}
          aoFechar={() => setConvite(null)}
        />
      )}

      {/*
        A nota do pé da tela já diz que não existe excluir e por quê, e o
        alerta do convite já diz que o link aparece uma vez e até quando vale —
        os dois são referenciados, não repetidos. O que falta é a tabela de
        papéis fora do formulário de criação (hoje ela só existe dentro do
        seletor) e o que o papel significa NAS OUTRAS telas, que é onde a
        diferença aparece.
      */}
      <BlocoDeAjuda
        secoes={[
          {
            id: 'como',
            titulo: 'Papéis e acesso',
            corpo: (
              <>
                <Text size="sm">
                  <strong>Leitor</strong> vê tudo e não muda nada. Serve para quem precisa
                  consultar conversa e protocolo sem responder pela base — e é o papel certo por
                  padrão, porque é o único que não quebra nada por engano.
                </Text>
                <Text size="sm">
                  <strong>Editor</strong> faz o trabalho do dia: escreve e corrige FAQ, resolve
                  lacunas, marca transbordo como atendido, edita a árvore do menu. O que ele{' '}
                  <strong>não</strong> faz: publicar a árvore do menu, reindexar a base e mexer em
                  usuários. As três ficam com o admin de propósito — são as que valem para todo
                  mundo de uma vez ou custam dinheiro.
                </Text>
                <Text size="sm">
                  <strong>Admin</strong> faz tudo, mais publicar a árvore, reindexar e convidar ou
                  desativar gente. Vale ter mais de um: com um só, uma FAQ corrigida fica fora da
                  busca até essa pessoa aparecer.
                </Text>
                <Text size="sm">
                  <strong>Ninguém define a senha de ninguém.</strong> Criar a pessoa gera um link
                  de convite para você mandar; ela escolhe a própria senha ao abrir. O link vale
                  uma vez e tem prazo — o aviso que aparece ao gerar diz até quando. Gerar outro
                  invalida o anterior, e é também o caminho para quem esqueceu a senha.
                </Text>
                <Text size="sm">
                  <strong>Trocar o papel ou desativar vale no mesmo instante</strong> — o servidor
                  confere a cada pedido, então não é preciso esperar a pessoa sair. Só um detalhe:
                  se ela estiver com o painel aberto, a tela dela continua mostrando os botões
                  antigos até recarregar. Clicar neles não funciona; o servidor recusa. Para tirar
                  o acesso de alguém agora, desativar basta.
                </Text>
                <Text size="sm" c="dimmed">
                  Quem está com <em>convite aberto</em> ainda não entrou nenhuma vez; com{' '}
                  <em>sem senha</em>, recebeu o convite e não terminou de definir a senha.
                </Text>
              </>
            ),
          },
        ]}
      />

      <Group gap="sm">
        <Button variant={criando ? 'default' : 'filled'} onClick={() => setCriando(!criando)}>
          {criando ? 'Cancelar' : 'Novo usuário'}
        </Button>
        <Button onClick={() => void carregar()}>Recarregar</Button>
        <Text size="sm" c="dimmed">
          {dados.itens.length} usuário(s)
        </Text>
      </Group>

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}

      {criando && (
        <FormNovo
          aoCriar={(criado) => {
            setCriando(false);
            setConvite({ gerado: criado.convite, nome: criado.usuario.nome });
            void carregar();
          }}
          aoErro={setErro}
        />
      )}

      <Table.ScrollContainer minWidth={860}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>E-mail</Table.Th>
              <Table.Th>Papel</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Último acesso</Table.Th>
              <Table.Th>Criado em</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {dados.itens.map((u) => (
              <Table.Tr key={u.id} opacity={u.ativo ? 1 : 0.55}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">{u.nome}</Text>
                    {u.id === dados.eu && (
                      <Text size="xs" c="dimmed">
                        · você
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{u.email}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={u.papel === 'admin' ? 'azul' : u.papel === 'editor' ? 'teal' : 'gray'}>
                    {u.papel}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="wrap">
                    <Text size="sm">{u.ativo ? 'ativo' : 'desativado'}</Text>
                    {u.semSenha && (
                      <Badge color="violet" size="xs" title="ainda não definiu senha">
                        sem senha
                      </Badge>
                    )}
                    {u.convitePendente && (
                      <Badge color="violet" size="xs" title="convite válido em aberto">
                        convite aberto
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{data(u.ultimoAcesso)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(u.criadoEm).toLocaleDateString('pt-BR')}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="wrap">
                    <Button
                      size="xs"
                      onClick={() => setEditando(editando === u.id ? null : u.id)}
                    >
                      Editar
                    </Button>
                    {u.ativo && (
                      <Button size="xs" onClick={() => void reenviarConvite(u)}>
                        {u.semSenha ? 'Reenviar convite' : 'Redefinir senha'}
                      </Button>
                    )}
                    {/* Desativar a si mesmo é recusado pelo servidor; não oferecer. */}
                    {u.id !== dados.eu && (
                      <Button size="xs" onClick={() => void alternarAtivo(u)}>
                        {u.ativo ? 'Desativar' : 'Reativar'}
                      </Button>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {editando && (
        <FormEdicao
          usuario={dados.itens.find((u) => u.id === editando) as UsuarioNaLista}
          souEu={editando === dados.eu}
          aoSalvar={() => {
            setEditando(null);
            void carregar();
          }}
          aoErro={setErro}
          aoFechar={() => setEditando(null)}
        />
      )}

      <Text size="xs" c="dimmed">
        Não existe excluir usuário: desativar cobre o caso, e a auditoria guarda o nome de quem
        publicou cada coisa — apagar o usuário perderia essa ligação.
      </Text>
    </Stack>
  );
}

// ---------------------------------------------------------------------------

/**
 * O link aparece UMA vez.
 *
 * O banco guarda só o hash do token, então recarregar a tela não o traz de
 * volta. Se o admin perder o link, o caminho é gerar outro — que invalida este.
 */
function LinkDeConvite({
  convite,
  nome,
  aoFechar,
}: {
  convite: ConviteGerado;
  nome: string;
  aoFechar: () => void;
}): JSX.Element {
  // Montado no NAVEGADOR, a partir da origem atual: é o endereço por onde o
  // painel está sendo acessado de verdade, e não depende de configuração.
  const link = `${window.location.origin}/?convite=${convite.token}`;

  return (
    <Alert color="violet" variant="light" withCloseButton onClose={aoFechar}>
      <Stack gap="xs" align="flex-start">
        <Text size="sm">
          <strong>Convite para {nome}.</strong> Copie o link agora e mande pelo canal que preferir
          — ele aparece <strong>uma única vez</strong>. Vale até{' '}
          {new Date(convite.expiraEm).toLocaleString('pt-BR')}.
        </Text>
        <Code block style={{ wordBreak: 'break-all' }}>
          {link}
        </Code>
        <Group gap="xs">
          <CopyButton value={link} timeout={4_000}>
            {({ copied, copy }) => (
              <Button size="xs" variant={copied ? 'light' : 'filled'} onClick={copy}>
                {copied ? 'Copiado ✓' : 'Copiar link'}
              </Button>
            )}
          </CopyButton>
          <Text size="xs" c="dimmed">
            Quem abrir o link define a própria senha. Perdeu? Gere outro — o anterior deixa de
            valer.
          </Text>
        </Group>
      </Stack>
    </Alert>
  );
}

function FormNovo({
  aoCriar,
  aoErro,
}: {
  aoCriar: (criado: UsuarioCriado) => void;
  aoErro: (mensagem: string) => void;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState<Papel>('leitor');
  const [salvando, setSalvando] = useState(false);

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      aoCriar(
        await api.post<UsuarioCriado>('/admin/usuarios', {
          nome: nome.trim(),
          email: email.trim(),
          papel,
        }),
      );
    } catch (e) {
      aoErro(explicarMotivo(e, 'falha ao criar o usuário'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Title order={2} size="h5">
          Novo usuário
        </Title>
        <TextInput
          label="Nome"
          value={nome}
          data-autofocus
          onChange={(e) => setNome(e.currentTarget.value)}
        />
        <TextInput
          label="E-mail"
          description="é o login da pessoa"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <Select
          label="Papel"
          w={360}
          allowDeselect={false}
          value={papel}
          onChange={(v) => setPapel((v ?? 'leitor') as Papel)}
          data={PAPEIS.map((p) => ({ value: p.valor, label: `${p.rotulo} — ${p.descricao}` }))}
        />

        <Alert color="gray" variant="light" title="Você não define a senha">
          Ao salvar, o painel gera um link de convite para você enviar; a pessoa escolhe a própria
          senha. Senha escolhida por terceiro vira senha compartilhada por WhatsApp, e ninguém
          troca depois.
        </Alert>

        <Group>
          <Button
            variant="filled"
            loading={salvando}
            disabled={nome.trim().length < 2 || !email.includes('@')}
            onClick={() => void salvar()}
          >
            Criar e gerar convite
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

function FormEdicao({
  usuario,
  souEu,
  aoSalvar,
  aoErro,
  aoFechar,
}: {
  usuario: UsuarioNaLista;
  souEu: boolean;
  aoSalvar: () => void;
  aoErro: (mensagem: string) => void;
  aoFechar: () => void;
}): JSX.Element {
  const [nome, setNome] = useState(usuario.nome);
  const [email, setEmail] = useState(usuario.email);
  const [papel, setPapel] = useState<Papel>(usuario.papel);
  const [salvando, setSalvando] = useState(false);

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      await api.patch(`/admin/usuarios/${usuario.id}`, {
        nome: nome.trim(),
        email: email.trim(),
        ...(souEu ? {} : { papel }),
      });
      aoSalvar();
    } catch (e) {
      aoErro(explicarMotivo(e, 'falha ao salvar'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal opened onClose={aoFechar} title={`Editar ${usuario.nome}`} centered>
      <Stack gap="sm">
        <TextInput
          label="Nome"
          value={nome}
          data-autofocus
          onChange={(e) => setNome(e.currentTarget.value)}
        />
        <TextInput
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        {email !== usuario.email && (
          <Alert color="orange" variant="light" title="O e-mail é o login desta pessoa">
            Trocando, ela entra com o novo — avise antes, senão vira um acesso perdido.
          </Alert>
        )}

        {souEu ? (
          <Text size="xs" c="dimmed">
            Papel: <strong>{usuario.papel}</strong> — ninguém altera o próprio papel. Peça a outro
            admin; é o que evita um rebaixamento irreversível por engano.
          </Text>
        ) : (
          <Select
            label="Papel"
            allowDeselect={false}
            value={papel}
            onChange={(v) => setPapel((v ?? usuario.papel) as Papel)}
            data={PAPEIS.map((p) => ({ value: p.valor, label: `${p.rotulo} — ${p.descricao}` }))}
          />
        )}

        <Group justify="flex-end">
          <Button onClick={aoFechar}>Cancelar</Button>
          <Button variant="filled" loading={salvando} onClick={() => void salvar()}>
            Salvar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
